import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { datTokenUsageApi, type DatTokenUsageItem, type DatTokenUsageSummary } from './api'

const PAGE_SIZE = 50

// Friendly labels + Joy colors per trigger source. Unknown triggers fall back to neutral.
const TRIGGER_META: Record<string, { label: string; color: 'primary' | 'success' | 'warning' | 'danger' | 'neutral' }> = {
    mobile_search: { label: 'Mobile Search', color: 'primary' },
    ai_hub: { label: 'AI Hub', color: 'success' },
    box_truck: { label: 'Box Truck', color: 'warning' },
    load_hub: { label: 'Load Hub', color: 'primary' },
    gis: { label: 'GIS', color: 'warning' },
    radar: { label: 'Load Radar', color: 'danger' },
    heatmap: { label: 'Heatmap', color: 'neutral' },
    unknown: { label: 'Unknown', color: 'neutral' },
}

// Keep in sync with DatTokenTrigger (@numeo_private/utils). Rows written before a label
// existed still render — triggerLabel falls back to the raw key — so an older `unknown`
// row from the admin boards stays visible next to the newer load_hub / box_truck ones.
const TRIGGER_OPTIONS = ['mobile_search', 'ai_hub', 'box_truck', 'load_hub', 'gis', 'radar', 'heatmap', 'unknown']

function triggerLabel(key: string): string {
    return TRIGGER_META[key]?.label ?? key
}
function triggerColor(key: string): 'primary' | 'success' | 'warning' | 'danger' | 'neutral' {
    return TRIGGER_META[key]?.color ?? 'neutral'
}

function fmt(n: number): string {
    return (n || 0).toLocaleString()
}

/** Small stat card for the summary row. */
function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 180, gap: 0.5 }}>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'neutral.500' }}>
                {label}
            </Typography>
            <Typography level="h3">{value}</Typography>
            {hint && (
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {hint}
                </Typography>
            )}
        </Card>
    )
}

export default function DatTokenUsage() {
    const [rows, setRows] = React.useState<DatTokenUsageItem[]>([])
    const [summary, setSummary] = React.useState<DatTokenUsageSummary>({ totalUses: 0, uniqueTokens: 0, byTrigger: {} })
    const [page, setPage] = React.useState(1)
    const [totalPages, setTotalPages] = React.useState(0)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    // Bumped by Refresh so the fetch effect re-runs even when no filter/page changed
    // (otherwise React bails on identical setState and the effect never fires).
    const [reloadKey, setReloadKey] = React.useState(0)

    // Draft filters (typed into) vs applied filters (drive the fetch).
    const [searchFilter, setSearchFilter] = React.useState('')
    const [projectFilter, setProjectFilter] = React.useState('')
    const [triggerFilter, setTriggerFilter] = React.useState('')
    const [dateFromFilter, setDateFromFilter] = React.useState('')
    const [dateToFilter, setDateToFilter] = React.useState('')
    const [sortBy, setSortBy] = React.useState<'uses' | 'userId'>('uses')

    const [appliedSearch, setAppliedSearch] = React.useState('')
    const [appliedProject, setAppliedProject] = React.useState('')
    const [appliedTrigger, setAppliedTrigger] = React.useState('')
    const [appliedFrom, setAppliedFrom] = React.useState('')
    const [appliedTo, setAppliedTo] = React.useState('')

    const applyFilters = () => {
        setPage(1)
        setAppliedSearch(searchFilter.trim())
        setAppliedProject(projectFilter.trim())
        setAppliedTrigger(triggerFilter)
        setAppliedFrom(dateFromFilter)
        setAppliedTo(dateToFilter)
    }

    const clearFilters = () => {
        setSearchFilter('')
        setProjectFilter('')
        setTriggerFilter('')
        setDateFromFilter('')
        setDateToFilter('')
        setAppliedSearch('')
        setAppliedProject('')
        setAppliedTrigger('')
        setAppliedFrom('')
        setAppliedTo('')
        setPage(1)
    }

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFilters()
        }
    }

    React.useEffect(() => {
        const isInitialLoad = page === 1 && !appliedSearch && !appliedProject && !appliedTrigger && !appliedFrom && !appliedTo && sortBy === 'uses'
        if (isInitialLoad) setLoading(true)
        else setFetching(true)
        setError(null)

        datTokenUsageApi
            .getUsage({
                page,
                pageSize: PAGE_SIZE,
                sortBy,
                ...(appliedSearch ? { search: appliedSearch } : {}),
                ...(appliedProject ? { project: appliedProject } : {}),
                ...(appliedTrigger ? { trigger: appliedTrigger } : {}),
                ...(appliedFrom ? { fromDay: appliedFrom } : {}),
                ...(appliedTo ? { toDay: appliedTo } : {}),
            })
            .then((data) => {
                setRows(data.rows)
                setSummary(data.summary)
                setTotalPages(data.totalPages)
            })
            .catch((e) => {
                setRows([])
                setSummary({ totalUses: 0, uniqueTokens: 0, byTrigger: {} })
                setTotalPages(0)
                setError(e?.response?.data?.message || e?.message || 'Failed to load DAT token usage')
            })
            .finally(() => {
                setLoading(false)
                setFetching(false)
            })
    }, [page, appliedSearch, appliedProject, appliedTrigger, appliedFrom, appliedTo, sortBy, reloadKey])

    // Clamp page when totalPages shrinks after a filter change.
    React.useEffect(() => {
        if (totalPages > 0 && page > totalPages) setPage(1)
    }, [totalPages, page])

    // Triggers present in the current window, biggest first — for the summary card.
    const summaryTriggers = React.useMemo(() => Object.entries(summary.byTrigger).sort((a, b) => b[1] - a[1]), [summary.byTrigger])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
                <CircularProgress />
                <Typography>Loading DAT token usage…</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <VpnKeyIcon />
                        <Typography level="h2" component="h1">
                            DAT Token Usage
                        </Typography>
                        {fetching && <CircularProgress size="sm" />}
                    </Box>
                    <Typography level="body-sm" sx={{ color: 'neutral.500', mt: 0.5 }}>
                        How often each pooled DAT token is drawn for searches (Mobile, AI Hub, GIS, Radar, Heatmap).
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" startDecorator={<RefreshIcon />} disabled={fetching} onClick={() => setReloadKey((k) => k + 1)}>
                        Refresh
                    </Button>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages || 1}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page >= totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            {/* Summary cards */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                <StatCard label="Total Uses" value={fmt(summary.totalUses)} hint="Across the selected window" />
                <StatCard label="Unique Tokens" value={fmt(summary.uniqueTokens)} hint="Distinct pooled DAT tokens used" />
                <Card variant="outlined" sx={{ flex: 2, minWidth: 260, gap: 0.5 }}>
                    <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'neutral.500' }}>
                        Uses by Source
                    </Typography>
                    {summaryTriggers.length === 0 ? (
                        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                            No usage in this window
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                            {summaryTriggers.map(([key, count]) => (
                                <Chip key={key} size="sm" variant="soft" color={triggerColor(key)}>
                                    {triggerLabel(key)}: {fmt(count)}
                                </Chip>
                            ))}
                        </Box>
                    )}
                </Card>
            </Box>

            {/* Filters */}
            <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, width: '100%', alignItems: 'flex-end' }}>
                    <FormControl sx={{ flex: 1.5, minWidth: 200 }} size="sm">
                        <FormLabel>Search (user ID)</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Token owner user ID…"
                            startDecorator={<SearchIcon />}
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1.5, minWidth: 200 }} size="sm">
                        <FormLabel>Project</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Project name starts with…"
                            startDecorator={<SearchIcon />}
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1, minWidth: 150 }} size="sm">
                        <FormLabel>Source</FormLabel>
                        <Select size="sm" placeholder="All sources" value={triggerFilter} onChange={(_, val) => setTriggerFilter(val || '')}>
                            <Option value="">All sources</Option>
                            {TRIGGER_OPTIONS.map((t) => (
                                <Option key={t} value={t}>
                                    {triggerLabel(t)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1, minWidth: 130 }} size="sm">
                        <FormLabel>From</FormLabel>
                        <Input size="sm" type="date" value={dateFromFilter} onChange={(e) => setDateFromFilter(e.target.value)} />
                    </FormControl>
                    <FormControl sx={{ flex: 1, minWidth: 130 }} size="sm">
                        <FormLabel>To</FormLabel>
                        <Input size="sm" type="date" value={dateToFilter} onChange={(e) => setDateToFilter(e.target.value)} />
                    </FormControl>
                    <FormControl sx={{ flex: 1, minWidth: 140 }} size="sm">
                        <FormLabel>Sort by</FormLabel>
                        <Select size="sm" value={sortBy} onChange={(_, val) => setSortBy((val as 'uses' | 'userId') || 'uses')}>
                            <Option value="uses">Most used</Option>
                            <Option value="userId">User ID</Option>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
                        <Button size="sm" variant="solid" color="primary" onClick={applyFilters}>
                            Apply
                        </Button>
                        <Button size="sm" variant="outlined" color="neutral" onClick={clearFilters}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Box>

            {error && (
                <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'sm', mb: 1 }}>
                    <Typography level="body-sm" color="danger">
                        {error}
                    </Typography>
                </Sheet>
            )}

            {/* Table */}
            <Sheet variant="outlined" sx={{ width: '100%', borderRadius: 'sm', overflow: 'auto', minHeight: 0 }}>
                <Table
                    aria-label="DAT token usage"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '6px',
                        '--TableCell-paddingX': '10px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 60, padding: '12px 10px' }}>#</th>
                            <th style={{ width: 200, padding: '12px 10px' }}>Project</th>
                            <th style={{ padding: '12px 10px' }}>Token Owner (User ID)</th>
                            <th style={{ width: 120, padding: '12px 10px', textAlign: 'right' }}>Total Uses</th>
                            <th style={{ width: 340, padding: '12px 10px' }}>By Source</th>
                            <th style={{ width: 110, padding: '12px 10px', textAlign: 'right' }}>Active Days</th>
                            <th style={{ width: 130, padding: '12px 10px' }}>Last Used</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => {
                            const rank = (page - 1) * PAGE_SIZE + index + 1
                            const triggers = Object.entries(row.byTrigger).sort((a, b) => b[1] - a[1])
                            return (
                                <tr key={row.userId}>
                                    <td>
                                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                                            {rank}
                                        </Typography>
                                    </td>
                                    <td>
                                        {row.projectName ? (
                                            <Tooltip title={row.companyName && row.companyName !== row.projectName ? row.companyName : row.projectName}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography level="body-sm" noWrap>
                                                        {row.projectName}
                                                    </Typography>
                                                    {row.companyName && row.companyName !== row.projectName && (
                                                        <Typography level="body-xs" sx={{ color: 'neutral.500' }} noWrap>
                                                            {row.companyName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography level="body-xs" sx={{ color: 'neutral.400', fontStyle: 'italic' }}>
                                                Unmapped
                                            </Typography>
                                        )}
                                    </td>
                                    <td>
                                        <Tooltip title={row.userId}>
                                            <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                                                {row.userId}
                                            </Typography>
                                        </Tooltip>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Typography level="body-sm" fontWeight="lg">
                                            {fmt(row.totalUses)}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {triggers.map(([key, count]) => (
                                                <Chip key={key} size="sm" variant="soft" color={triggerColor(key)}>
                                                    {triggerLabel(key)}: {fmt(count)}
                                                </Chip>
                                            ))}
                                        </Box>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Typography level="body-sm">{fmt(row.activeDays)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.lastUsedDay || 'N/A'}</Typography>
                                    </td>
                                </tr>
                            )
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                                    <Typography level="body-sm" color="neutral">
                                        No DAT token usage found for the selected filters.
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>
        </React.Fragment>
    )
}
